import { useState, type FormEvent } from 'react';
import type { Capability } from '@busbook/shared';
import { Alert, Button, Card, Field, Input, useConfirm } from '@busbook/ui';
import { NAV, Page } from '../components/Shell.js';
import { Column, DataTable, Drawer } from '../components/DataTable.js';
import { errorMessage, useResource } from '../lib/useResource.js';
import { api } from '../lib/api.js';
import { useSession } from '../lib/session.js';

/**
 * Custom staff roles (DECISIONS D77). A role is a row an operator can create, edit, delete
 * and reset, gated on staff:manage the same way Staff is — this is governance of the
 * account, not its identity or its money, which is why it sits beside Staff rather than
 * under Business.
 */

interface RoleRow {
  id: string;
  name: string;
  description: string | null;
  builtInKey: string | null;
  capabilities: Capability[];
  staffCount: number;
  isDefault: boolean | null;
}

/** The navigation's own section labels, keyed by the section's i18n key. Roles.tsx does not
 *  use i18n for its body text (matching Promotions.tsx's convention), so this is the one
 *  place an English label is attached to each section NAV already defines. */
const SECTION_LABELS: Record<string, string> = {
  'nav.section.today': 'Today',
  'nav.section.network': 'Network',
  'nav.section.business': 'Business',
  'nav.section.admin': 'Admin',
};

/**
 * Derived from Shell.tsx's own NAV, so the grid reads as "what this person can reach"
 * without keeping a second, hand-maintained copy of the capability taxonomy that NAV
 * already encodes per destination.
 *
 * trips:operate, refunds:issue and cash:review gate inline actions (delay/cancel/check-in,
 * the refund button, cash review), not a page of their own, so none has a NAV entry to
 * derive from — added explicitly to Today rather than silently missing from the editor.
 */
const NOT_IN_NAV: Capability[] = ['trips:operate', 'refunds:issue', 'cash:review'];

export const CAPABILITY_GROUPS: Array<{ label: string; capabilities: Capability[] }> = NAV.map((section) => ({
  label: SECTION_LABELS[section.key] ?? section.key,
  capabilities: [...new Set(section.items.map((item) => item.capability))],
})).map((group) => (group.label === 'Today' ? { ...group, capabilities: [...group.capabilities, ...NOT_IN_NAV] } : group));

const CAPABILITY_LABELS: Record<Capability, string> = {
  'dashboard:view': 'View the dashboard',
  'bookings:view': 'View bookings',
  'trips:operate': 'Operate trips (delay, cancel, check-in)',
  'disputes:manage': 'Handle disputes',
  'refunds:issue': 'Issue refunds',
  'cash:review': 'Review cash deposits',
  'catalog:manage': 'Manage fleet, network & timetables',
  'branding:manage': 'Manage branding & promotions',
  'billing:view': 'View billing',
  'settings:manage': 'Manage settings',
  'staff:manage': 'Manage staff & roles',
};

interface Draft {
  name: string;
  description: string;
  capabilities: Capability[];
}

function draftOf(row: RoleRow): Draft {
  return { name: row.name, description: row.description ?? '', capabilities: row.capabilities };
}

const BLANK: Draft = { name: '', description: '', capabilities: [] };

export function Roles() {
  const { user } = useSession();
  const { data, error, loading, reload } = useResource<RoleRow[]>('/admin/roles');
  const [editing, setEditing] = useState<RoleRow | 'new' | null>(null);
  const { ask, dialog } = useConfirm();
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const mine = user?.capabilities ?? [];

  async function remove(row: RoleRow) {
    const yes = await ask({
      title: `Delete “${row.name}”?`,
      body: 'This cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!yes) return;
    try {
      await api.delete(`/admin/roles/${row.id}`);
      setMessage({ tone: 'success', text: 'Role deleted.' });
      reload();
    } catch (err) {
      setMessage({ tone: 'error', text: errorMessage(err) });
    }
  }

  async function reset(row: RoleRow) {
    const yes = await ask({
      title: `Reset “${row.name}” to its default?`,
      body: 'Capabilities return to what this role started with. Name, description and who holds it are untouched.',
      confirmLabel: 'Reset',
    });
    if (!yes) return;
    try {
      await api.post(`/admin/roles/${row.id}/reset`, {});
      setMessage({ tone: 'success', text: 'Role reset to its default.' });
      reload();
    } catch (err) {
      setMessage({ tone: 'error', text: errorMessage(err) });
    }
  }

  const columns: Column<RoleRow>[] = [
    {
      key: 'name',
      header: 'Role',
      render: (row) => (
        <div>
          <p className="font-medium">
            {row.name}
            {!row.builtInKey && (
              <span className="ml-2 text-xs font-normal" style={{ color: 'var(--color-muted)' }}>
                custom
              </span>
            )}
          </p>
          {row.description && (
            <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
              {row.description}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'capabilities',
      header: 'Capabilities',
      render: (row) => <span className="tabular">{row.capabilities.length}</span>,
    },
    {
      key: 'staff',
      header: 'Staff',
      render: (row) => <span className="tabular">{row.staffCount}</span>,
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <span className="flex flex-wrap justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => setEditing(row)}>
            Edit
          </Button>
          {row.builtInKey && (
            <Button size="sm" variant="ghost" disabled={row.isDefault === true} onClick={() => void reset(row)}>
              Reset to default
            </Button>
          )}
          {!row.builtInKey && (
            <Button
              size="sm"
              variant="ghost"
              disabled={row.staffCount > 0}
              onClick={() => void remove(row)}
            >
              Delete
            </Button>
          )}
        </span>
      ),
    },
  ];

  return (
    <Page
      title="Roles"
      description="What the four built-in roles grant, and any role your business needs that they don't cover."
      actions={
        <Button size="sm" onClick={() => setEditing('new')}>
          New role
        </Button>
      }
    >
      {dialog}
      {message && (
        <div className="mb-4">
          <Alert tone={message.tone}>{message.text}</Alert>
        </div>
      )}
      {error ? (
        <Alert tone="error">{errorMessage(error)}</Alert>
      ) : (
        <Card>
          <DataTable
            columns={columns}
            rows={data ?? []}
            loading={loading}
            empty={{ title: 'No roles yet', description: '' }}
            rowKey={(row) => row.id}
          />
        </Card>
      )}

      {editing && (
        <RoleDrawer
          row={editing === 'new' ? null : editing}
          myCapabilities={mine}
          onClose={() => setEditing(null)}
          onSaved={(text) => {
            setEditing(null);
            setMessage({ tone: 'success', text });
            reload();
          }}
        />
      )}
    </Page>
  );
}

function RoleDrawer({
  row,
  myCapabilities,
  onClose,
  onSaved,
}: {
  row: RoleRow | null;
  myCapabilities: Capability[];
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => (row ? draftOf(row) : BLANK));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set(patch: Partial<Draft>) {
    setDraft((current) => ({ ...current, ...patch }));
    setError(null);
  }

  function toggle(capability: Capability, checked: boolean) {
    set({
      capabilities: checked
        ? [...draft.capabilities, capability]
        : draft.capabilities.filter((c) => c !== capability),
    });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = {
        name: draft.name.trim(),
        description: draft.description.trim(),
        capabilities: draft.capabilities,
      };
      if (row) await api.put(`/admin/roles/${row.id}`, body);
      else await api.post('/admin/roles', body);
      onSaved(row ? 'Role updated.' : 'Role created.');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer open title={row ? `Edit ${row.name}` : 'New role'} onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Name" htmlFor="r-name">
          <Input id="r-name" value={draft.name} onChange={(e) => set({ name: e.target.value })} required />
        </Field>

        <Field label="Description" htmlFor="r-desc" hint="Shown on the Staff screen when assigning this role.">
          <Input id="r-desc" value={draft.description} onChange={(e) => set({ description: e.target.value })} />
        </Field>

        <div className="flex flex-col gap-4">
          {CAPABILITY_GROUPS.map((group) => (
            <div key={group.label}>
              <p className="mb-1 text-xs font-medium uppercase" style={{ color: 'var(--color-faint)' }}>
                {group.label}
              </p>
              <div className="flex flex-col gap-1.5">
                {group.capabilities.map((capability) => {
                  // Shown disabled rather than hidden: hiding it would make the screen lie
                  // about what this role could be given to someone with wider authority.
                  const mayGrant = myCapabilities.includes(capability);
                  return (
                    <label
                      key={capability}
                      className="flex items-center gap-2 text-sm"
                      style={mayGrant ? undefined : { color: 'var(--color-faint)' }}
                    >
                      <input
                        type="checkbox"
                        checked={draft.capabilities.includes(capability)}
                        disabled={!mayGrant}
                        onChange={(e) => toggle(capability, e.target.checked)}
                      />
                      {CAPABILITY_LABELS[capability]}
                      {!mayGrant && ' (you do not hold this)'}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {error && <Alert tone="error">{error}</Alert>}

        <div className="flex gap-2">
          <Button type="submit" loading={busy} disabled={draft.capabilities.length === 0}>
            {row ? 'Save' : 'Create'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Drawer>
  );
}
